import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState } from 'react';
import { TimelineDot } from './TimelineDot';
import { LeafCard } from './LeafCard';
import styles from './container-cards.module.css';

/**
 * Pixel ports of kaneai's ConditionalBlockV4 + UntilBlockV4 (read-only
 * subset — no draft mode, no editing, no live-execution markers). Every
 * value quoted from the distilled source spec; deviations flagged inline.
 */

/** Mirror of StepsPanel's RowContext (type-only import avoids a runtime
 * cycle — StepsPanel imports these cards at value level). */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var ASSERTION_OPERATOR_DISPLAY = {
  equals: '==',
  not_equals: '!=',
  contains: 'contains',
  not_contains: 'not contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  not_starts_with: 'not starts with',
  not_ends_with: 'not ends with',
  greater_than: '>',
  less_than: '<',
  greater_than_or_equal: '>=',
  less_than_or_equal: '<='
};
var Chevron = function Chevron(_ref) {
  var expanded = _ref.expanded;
  return /*#__PURE__*/_jsx("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    "aria-hidden": "true",
    style: {
      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
      transition: 'transform 150ms'
    },
    children: /*#__PURE__*/_jsx("path", {
      d: "M6 4L10 8L6 12",
      stroke: "#57606A",
      strokeWidth: "1.5",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })
  });
};

/** kaneai IfElseIcon (quoted). */
var IfElseIcon = function IfElseIcon(_ref2) {
  var c = _ref2.c;
  return /*#__PURE__*/_jsx("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    "aria-hidden": "true",
    children: /*#__PURE__*/_jsx("path", {
      d: "M13.1422 10.1149V4.91004H11.4073M11.4074 10.1149H14.8772M6.7723 13.1322L9.30608 2.86768M2.85799 4.91004C3.81617 4.91004 4.59294 5.6868 4.59294 6.64498V8.37993C4.59294 9.33811 3.81617 10.1149 2.85799 10.1149C1.89981 10.1149 1.12305 9.33811 1.12305 8.37993V6.64498C1.12305 5.6868 1.89981 4.91004 2.85799 4.91004Z",
      stroke: c,
      strokeWidth: "1.25",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })
  });
};

/** kaneai ElseBranchIcon (quoted — 16×19, viewBox 0 0 20 24). */
var ElseBranchIcon = function ElseBranchIcon(_ref3) {
  var c = _ref3.c;
  return /*#__PURE__*/_jsx("svg", {
    width: "16",
    height: "19",
    viewBox: "0 0 20 24",
    fill: "none",
    "aria-hidden": "true",
    children: /*#__PURE__*/_jsx("path", {
      d: "M12.3333 6.45996H15.25M15.25 6.45996V9.37663M15.25 6.45996L11.75 9.95996M7.66667 6.45996H4.75M4.75 6.45996V9.37663M4.75 6.45996L9.31633 11.0263C9.5361 11.246 9.70978 11.5073 9.82718 11.795C9.94458 12.0827 10.0033 12.3909 10 12.7016V17.5433",
      stroke: c,
      strokeWidth: "1.25",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })
  });
};

/** kaneai UntilIcon (quoted). */
var UntilIcon = function UntilIcon(_ref4) {
  var c = _ref4.c;
  return /*#__PURE__*/_jsxs("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("path", {
      d: "M2 5.5C2 4.12 3.12 3 4.5 3H11L9.5 1.5M14 10.5C14 11.88 12.88 13 11.5 13H5L6.5 14.5",
      stroke: c,
      strokeWidth: "1.3",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }), /*#__PURE__*/_jsx("path", {
      d: "M2 5.5V8M14 10.5V8",
      stroke: c,
      strokeWidth: "1.3",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })]
  });
};
var Verdict = function Verdict(_ref5) {
  var passed = _ref5.passed;
  return passed ? /*#__PURE__*/_jsxs("span", {
    className: styles.verdict,
    style: {
      color: '#1A7F37'
    },
    children: [/*#__PURE__*/_jsx("svg", {
      width: "16",
      height: "16",
      viewBox: "0 0 16 16",
      fill: "none",
      "aria-hidden": "true",
      children: /*#__PURE__*/_jsx("path", {
        d: "M4 8l3 3 5-6",
        stroke: "#1A7F37",
        strokeWidth: "1.5",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      })
    }), "True"]
  }) : /*#__PURE__*/_jsxs("span", {
    className: styles.verdict,
    style: {
      color: '#CF222E'
    },
    children: [/*#__PURE__*/_jsx("svg", {
      width: "16",
      height: "16",
      viewBox: "0 0 16 16",
      fill: "none",
      "aria-hidden": "true",
      children: /*#__PURE__*/_jsx("path", {
        d: "M4 4l8 8M12 4l-8 8",
        stroke: "#CF222E",
        strokeWidth: "1.5",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      })
    }), "False"]
  });
};

/** Inner child row — same 24px rail geometry as the block's ChildRow
 * (18px first-row spacer / 22px connector, TimelineDot, flat-index
 * colors), content is the ordinary LeafCard. */
function NestedRow(_ref6) {
  var step = _ref6.step,
    childIdx = _ref6.childIdx,
    isLast = _ref6.isLast,
    ctx = _ref6.ctx,
    dimmed = _ref6.dimmed;
  var i = ctx.indexOf(step.key);
  var status = ctx.displayed(step);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.nestedRow,
    style: dimmed ? {
      opacity: 0.5
    } : undefined,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.railColumn,
      children: [childIdx === 0 ? /*#__PURE__*/_jsx("div", {
        style: {
          height: 18
        }
      }) : /*#__PURE__*/_jsx("div", {
        className: styles.railLine,
        style: {
          height: 22,
          background: ctx.topColor(i)
        }
      }), /*#__PURE__*/_jsx(TimelineDot, {
        status: status
      }), !isLast ? /*#__PURE__*/_jsx("div", {
        className: "".concat(styles.railLine, " ").concat(styles.railFill),
        style: {
          background: ctx.bottomColor(i)
        }
      }) : /*#__PURE__*/_jsx("div", {
        className: styles.railFillHidden
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: styles.nestedContent,
      children: /*#__PURE__*/_jsx(LeafCard, {
        step: step,
        status: status,
        selected: ctx.selectedKey === step.key,
        chipHidden: ctx.chipHidden(status),
        onSelect: ctx.onSelect
      })
    })]
  });
}
var blockChromeColor = function blockChromeColor(status) {
  return status === 'failed' ? '#CF222E' : status === 'passed' ? '#1A7F37' : '#6E7781';
};

/** ConditionalBlockV4 port — the rich if/else card. */
export function IfElseCard(_ref7) {
  var step = _ref7.step,
    ctx = _ref7.ctx;
  var vm = step.ifElse;
  var _useState = useState(function () {
      return new Set();
    }),
    _useState2 = _slicedToArray(_useState, 2),
    collapsed = _useState2[0],
    setCollapsed = _useState2[1];
  if (!vm) return null;
  var status = ctx.displayed(step);
  var iconColor = blockChromeColor(status);
  var resolved = vm.resolvedBranchIndex;
  return /*#__PURE__*/_jsx("div", {
    className: styles.ifCard,
    role: "group",
    "aria-label": "If / else block",
    children: vm.branches.map(function (br, bi) {
      var _br$subCheck, _ASSERTION_OPERATOR_D, _br$subCheck2, _br$subCheck$right;
      var isElse = br.label === 'Else';
      var isPassed = resolved !== null ? bi === resolved : br.passed === true;
      var showResult = resolved !== null || !isElse || isPassed;
      // Unresolved branches grey out (kaneai rule) — resolved known and
      // this branch isn't it.
      var dimmed = resolved !== null && bi !== resolved;
      var expanded = !collapsed.has(bi);
      var operator = (_br$subCheck = br.subCheck) !== null && _br$subCheck !== void 0 && _br$subCheck.operator ? (_ASSERTION_OPERATOR_D = ASSERTION_OPERATOR_DISPLAY[br.subCheck.operator]) !== null && _ASSERTION_OPERATOR_D !== void 0 ? _ASSERTION_OPERATOR_D : br.subCheck.operator : '==';
      return /*#__PURE__*/_jsxs("div", {
        className: bi > 0 ? styles.branchSeparated : undefined,
        children: [/*#__PURE__*/_jsxs("div", {
          className: styles.branchHeader,
          children: [br.steps.length > 0 && /*#__PURE__*/_jsx("button", {
            type: "button",
            className: styles.chevronBtn,
            "aria-expanded": expanded,
            "aria-label": "".concat(br.label, " branch steps"),
            onClick: function onClick() {
              return setCollapsed(function (prev) {
                var next = new Set(prev);
                if (next.has(bi)) next["delete"](bi);else next.add(bi);
                return next;
              });
            },
            children: /*#__PURE__*/_jsx(Chevron, {
              expanded: expanded
            })
          }), /*#__PURE__*/_jsx("span", {
            className: styles.branchIcon,
            children: isElse ? /*#__PURE__*/_jsx(ElseBranchIcon, {
              c: isPassed ? '#1A7F37' : '#6E7781'
            }) : /*#__PURE__*/_jsx(IfElseIcon, {
              c: iconColor
            })
          }), /*#__PURE__*/_jsxs("span", {
            className: styles.labelArea,
            children: [/*#__PURE__*/_jsx("span", {
              className: styles.branchLabel,
              children: br.label
            }), !isElse && (_br$subCheck2 = br.subCheck) !== null && _br$subCheck2 !== void 0 && _br$subCheck2.left ? /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx("span", {
                className: styles.pill,
                title: br.subCheck.left,
                children: br.subCheck.left
              }), /*#__PURE__*/_jsx("span", {
                className: styles.operator,
                children: operator
              }), /*#__PURE__*/_jsx("span", {
                className: styles.pill,
                children: (_br$subCheck$right = br.subCheck.right) !== null && _br$subCheck$right !== void 0 ? _br$subCheck$right : ''
              })]
            }) : !isElse && br.condition ? /*#__PURE__*/_jsx("span", {
              className: styles.pillRaw,
              children: br.condition
            }) : null]
          }), showResult && /*#__PURE__*/_jsx(Verdict, {
            passed: isPassed
          })]
        }), br.steps.length === 0 ? /*#__PURE__*/_jsx("div", {
          className: styles.emptyRow,
          children: /*#__PURE__*/_jsx("span", {
            className: styles.emptyText,
            children: "No steps"
          })
        }) : expanded && /*#__PURE__*/_jsx("div", {
          className: styles.branchChildren,
          role: "list",
          children: br.steps.map(function (child, i) {
            return /*#__PURE__*/_jsx(NestedRow, {
              step: child,
              childIdx: i,
              isLast: i === br.steps.length - 1,
              ctx: ctx,
              dimmed: dimmed
            }, child.key);
          })
        })]
      }, bi);
    })
  });
}

/** UntilBlockV4 port — the retry-loop card. */
export function UntilCard(_ref8) {
  var step = _ref8.step,
    ctx = _ref8.ctx;
  var vm = step.until;
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    expanded = _useState4[0],
    setExpanded = _useState4[1];
  if (!vm) return null;
  var status = ctx.displayed(step);
  var failed = status === 'failed';
  var iconColor = failed ? '#CF222E' : status === 'passed' ? '#1A7F37' : '#636C76';
  // kaneai parity: block_label renders verbatim, nothing when absent
  // (user 2026-07-09: no invented "Until" text — the loop icon + pill
  // carry the meaning).
  var label = vm.label;
  var showResult = vm.conditionMet !== null && (status === 'passed' || status === 'failed');
  var stepCount = vm.steps.length;
  return /*#__PURE__*/_jsxs("div", {
    className: styles.untilCard,
    style: failed ? {
      borderColor: '#CF222E'
    } : undefined,
    role: "group",
    "aria-label": "Until block",
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.untilHeader,
      children: [stepCount > 0 && /*#__PURE__*/_jsx("button", {
        type: "button",
        className: "".concat(styles.chevronBtn, " ").concat(styles.untilChevron),
        "aria-expanded": expanded,
        "aria-label": "Until steps",
        onClick: function onClick() {
          return setExpanded(function (v) {
            return !v;
          });
        },
        children: /*#__PURE__*/_jsx(Chevron, {
          expanded: expanded
        })
      }), /*#__PURE__*/_jsx("span", {
        className: "".concat(styles.branchIcon, " ").concat(styles.untilIcon),
        children: /*#__PURE__*/_jsx(UntilIcon, {
          c: iconColor
        })
      }), /*#__PURE__*/_jsxs("span", {
        className: styles.untilTitle,
        children: [label && /*#__PURE__*/_jsx("span", {
          className: styles.branchLabel,
          children: label
        }), vm.condition && /*#__PURE__*/_jsxs(_Fragment, {
          children: [label ? ' ' : null, /*#__PURE__*/_jsx("span", {
            className: styles.pillRaw,
            title: vm.condition,
            children: vm.condition
          })]
        })]
      }), showResult && /*#__PURE__*/_jsx("span", {
        className: styles.untilResult,
        children: /*#__PURE__*/_jsx(Verdict, {
          passed: vm.conditionMet === true
        })
      })]
    }), stepCount > 0 && expanded && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("div", {
        className: styles.untilChildren,
        role: "list",
        children: vm.steps.map(function (child, i) {
          return /*#__PURE__*/_jsx(NestedRow, {
            step: child,
            childIdx: i,
            isLast: i === stepCount - 1,
            ctx: ctx,
            dimmed: false
          }, child.key);
        })
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.untilFooter,
        children: [vm.currentCount !== null && vm.currentCount > 0 && /*#__PURE__*/_jsx("span", {
          title: vm.maxRetries !== null ? "".concat(vm.currentCount, " of ").concat(vm.maxRetries, " iterations") : undefined,
          children: /*#__PURE__*/_jsxs("span", {
            className: styles.counterPill,
            children: [vm.currentCount, " ", vm.currentCount === 1 ? 'run' : 'runs']
          })
        }), /*#__PURE__*/_jsx("span", {
          title: "".concat(stepCount, " child step").concat(stepCount === 1 ? '' : 's'),
          children: /*#__PURE__*/_jsxs("span", {
            className: styles.counterPill,
            children: [stepCount, " ", stepCount === 1 ? 'step' : 'steps']
          })
        })]
      })]
    })]
  });
}